import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import VulnerabilitiesFiltersViewModel from "./ViewModels/VulnerabilitiesFiltersViewModel";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import VulnerabilityViewModel from "./ViewModels/VulnerabilityViewModel";
import Chip from "@mui/material/Chip";

interface IVulnerabilitiesFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function VulnerabilitiesFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: IVulnerabilitiesFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [vulnerabilitiesFilters, setVulnerabilitiesFilters] = useState<VulnerabilitiesFiltersViewModel>(callTableViewModel.filters.vulnerabilitiesFilters);
    const [vulnerabilityCategories, setVulnerabilityCategories] = useState<string[]>([]);
    const [vulnerabilities, setVulnerabilities] = useState<VulnerabilityViewModel[]>([]);

    useEffect(() => {
        if (open) {
            setVulnerabilitiesFilters(callTableViewModel.filters.vulnerabilitiesFilters);
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('Search', `GetVulnerabilityCategoryDropDown`, (data) => setVulnerabilityCategories(data));
            get('Search', `GetVulnerabilityDropDown`, (data) => setVulnerabilities(data));
        }
    }, [callTableViewModel.filters.client])

    useEffect(() => {
        // Only clear vulnerability if there is no category selected
        if (vulnerabilitiesFilters.vulnerabilityCategory.length === 0) {
            setVulnerabilitiesFilters({
                ...vulnerabilitiesFilters,
                vulnerability: [],
            });
        }
    }, [vulnerabilitiesFilters.vulnerabilityCategory])

    const inputProps: InputProps[] = [
        {
            name: 'vulnerabilityCategory',
            label: 'Vulnerabilitiy Category',
            value: vulnerabilitiesFilters.vulnerabilityCategory,
            options: vulnerabilityCategories,
            disabled: false,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'vulnerability',
            label: 'Vulnerability',
            value: vulnerabilitiesFilters.vulnerability,
            options: vulnerabilities
                .filter(f => vulnerabilitiesFilters.vulnerabilityCategory.includes(f.category))
                .map(m => m.vulnerability),
            disabled: vulnerabilitiesFilters.vulnerabilityCategory.length === 0
                || vulnerabilitiesFilters.vulnerabilityCategory === null,
            type: InputType.AutoCompleteMultiple
        },

    ];

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                {
                    inputProps.type === InputType.AutoCompleteMultiple && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <Autocomplete
                            key={inputProps.name}
                            multiple
                            fullWidth
                            options={inputProps.options!}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option}
                            value={inputProps.value as string[]}
                            onChange={(_event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setVulnerabilitiesFilters) }}
                            limitTags={3}
                            renderOption={(props, option, { selected }) => {
                                const { ...optionProps } = props;
                                return (
                                    <li {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                                const numTags = value.length;
                                const limitTags = 1;
                                return (
                                    <>
                                        {value.slice(0, limitTags).map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={index}
                                                label={option}
                                            />
                                        ))}

                                        {numTags > limitTags && ` +${numTags - limitTags}`}
                                    </>
                                );
                            }}
                        />
                    </>
                }
            </Grid>)
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.vulnerabilitiesFilters = vulnerabilitiesFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Vulnerabilities Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    {
                        inputProps.map(props => getInput(props))
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}
